import { HttpClient, HttpContext, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, concatMap, forkJoin, map, retry, startWith, switchMap, tap, throwError } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar'; // Import MatSnackBar
import { environment } from 'src/environments/environment';
import { v4 as uuidv4 } from 'uuid';

@Injectable({
  providedIn: 'root'
})
export class ApiFluxService {
  private baseUrl: string = environment.apiUrl;
  private httpOptions: {
    body?: any;
    headers: {
      [header: string]: string | string[];
    };
    context?: HttpContext;
    observe?: 'body';
    params?: HttpParams | {
      [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
    };
    reportProgress?: boolean;
    responseType: 'json';
    withCredentials?: boolean;
  } = {
    headers: { 'Content-Type': 'application/json' },
    responseType: 'json'
  };

  constructor(
    private http: HttpClient,
    private snackBar: MatSnackBar, // Inject MatSnackBar here
  ) {}

  public get<T>(url: string, retryCount: number = 0, notShowError?: boolean, showAccepted?:boolean):  Observable<{ firstResponse: HttpResponse<T>, secondResponse: HttpResponse<T> }> {
    const uuid = uuidv4();
    this.setHeader('RequestUuid', uuid);

    return this.http.get<T>(`${this.baseUrl}/${url}`, { ...this.httpOptions, observe: 'response' }).pipe(
      retry(retryCount),
      catchError(error => this.handleError(error, notShowError ?? false)),
      tap(response => {
        if (response.status === 202 && showAccepted) {
          this.snackBar.open('Richiesta presa in carico', 'close', {
            duration: 3000,
          });
        }
      }),
      concatMap(firstResponse =>
        this.http.get<T>(`${this.baseUrl}/flux/${uuid}`, { ...this.httpOptions, observe: 'response' }).pipe(
          retry(retryCount),
          catchError(error => this.handleError(error, notShowError ?? false)),
          map(secondResponse => ({ firstResponse, secondResponse })) // Restituisce entrambe le risposte come oggetto
        )
      )
    );
  }


  public post<T>(url: string, data: any, retryCount: number = 0, notShowError?: boolean, showAccepted?:boolean): Observable<{ firstResponse: HttpResponse<T>, secondResponse: HttpResponse<T> }> {
    const uuid = uuidv4();
    this.setHeader('RequestUuid', uuid);

    return this.http.post<T>(`${this.baseUrl}/${url}`, data, { ...this.httpOptions, observe: 'response' }).pipe(
      retry(retryCount),
      catchError(error => this.handleError(error, notShowError ?? false)),
      tap(response => {
        if (response.status === 202  && showAccepted) {
          this.snackBar.open('Richiesta presa in carico', 'close', {
            duration: 3000,
          });
        }
      }),
      concatMap(firstResponse =>
        this.http.get<T>(`${this.baseUrl}/flux/${uuid}`, { ...this.httpOptions, observe: 'response' }).pipe(
          retry(retryCount),
          catchError(error => this.handleError(error, notShowError ?? false)),
          map(secondResponse => ({ firstResponse, secondResponse })) // Restituisce entrambe le risposte come oggetto
        )
      )
    );
  }


  public put<T>(url: string, data: any, retryCount: number = 0,notShowError?:boolean):  Observable<{ firstResponse: HttpResponse<T>, secondResponse: HttpResponse<T> }>{
    const uuid = uuidv4();
    this.setHeader('RequestUuid', uuid);

    return this.http.put<T>(`${this.baseUrl}/${url}`, data, { ...this.httpOptions, observe: 'response' }).pipe(
      retry(retryCount),
      catchError(error => this.handleError(error, notShowError ?? false)),
      tap(response => {
        if (response.status === 202) {
          this.snackBar.open('Richiesta presa in carico', 'close', {
            duration: 3000,
          });
        }
      }),
      concatMap(firstResponse =>
        this.http.get<T>(`${this.baseUrl}/flux/${uuid}`, { ...this.httpOptions, observe: 'response' }).pipe(
          retry(retryCount),
          catchError(error => this.handleError(error, notShowError ?? false)),
          map(secondResponse => ({ firstResponse, secondResponse })) // Restituisce entrambe le risposte come oggetto
        )
      )
    );
  }

  public patch<T>(url: string, data: any, retryCount: number = 0,notShowError?:boolean):  Observable<{ firstResponse: HttpResponse<T>, secondResponse: HttpResponse<T> }>{
    const uuid = uuidv4();
    this.setHeader('RequestUuid', uuid);

    return this.http.patch<T>(`${this.baseUrl}/${url}`, data, { ...this.httpOptions, observe: 'response' }).pipe(
      retry(retryCount),
      catchError(error => this.handleError(error, notShowError ?? false)),
      tap(response => {
        if (response.status === 202) {
          this.snackBar.open('Richiesta presa in carico', 'close', {
            duration: 3000,
          });
        }
      }),
      concatMap(firstResponse =>
        this.http.get<T>(`${this.baseUrl}/flux/${uuid}`, { ...this.httpOptions, observe: 'response' }).pipe(
          retry(retryCount),
          catchError(error => this.handleError(error, notShowError ?? false)),
          map(secondResponse => ({ firstResponse, secondResponse })) // Restituisce entrambe le risposte come oggetto
        )
      )
    );
  }

  public delete<T>(url: string, retryCount: number = 0,notShowError?:boolean):  Observable<{ firstResponse: HttpResponse<T>, secondResponse: HttpResponse<T> }>{
    const uuid = uuidv4();
    this.setHeader('RequestUuid',uuid )

    return this.http.delete<T>(`${this.baseUrl}/${url}`, { ...this.httpOptions, observe: 'response' }).pipe(
      retry(retryCount),
      catchError(error => this.handleError(error, notShowError ?? false)),
      tap(response => {
        if (response.status === 202) {
          this.snackBar.open('Richiesta presa in carico', 'close', {
            duration: 3000,
          });
        }
      }),
      concatMap(firstResponse =>
        this.http.get<T>(`${this.baseUrl}/flux/${uuid}`, { ...this.httpOptions, observe: 'response' }).pipe(
          retry(retryCount),
          catchError(error => this.handleError(error, notShowError ?? false)),
          map(secondResponse => ({ firstResponse, secondResponse })) // Restituisce entrambe le risposte come oggetto
        )
      )
    );
  }

  public setHeader(key: string, value: string | undefined) {
    if (value) {
      if (this.httpOptions.headers) {
        this.httpOptions.headers[key] = value;
      } else {
        this.httpOptions.headers = {
          key: value
        };
      }
    } else {
      delete this.httpOptions.headers[key];
    }
  }

  private handleError(error: any, notShowError:boolean) {
    if(notShowError){
      return throwError(() => {
        return null;
      });
    }
    console.log(error)
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      // Get client-side error
      errorMessage = error.error.message;
    } /* else if (error.error.listaErrori) {
      // Get server-side error
      error.error.listaErrori.forEach((errorItem: { codiceErrore: string, descrizioneErrore: string }) => {
        this.snackBar.open(`Errore: ${errorItem.descrizioneErrore}`, 'close', {
          duration: 3000, // Optional: Set duration for snack bar
        });
      });
    } else if (error.error.codiceErrore) {
      this.snackBar.open(`Errore: ${error.error.descrizioneErrore}`, 'close', {
        duration: 3000,
      });
    } else if (error.error.error) {
      this.snackBar.open(`Errore: Mi dispiace, qualcosa non va. <br>(${error.error.status} - ${error.error.error})`, 'close', {
        duration: 3000,
      });
    } */ else {
      this.snackBar.open(`Errore: Mi dispiace, il server non risponde.`, 'close', {
        duration: 3000,
      });
    }


    return throwError(() => {
      return errorMessage;
    });

  }

}
